import type { CountryCode, LocaleCode } from '#types/locale'
import type { NetlifyCountry, SelectorSite } from '#types/locationSelector'

declare module 'vue-router' {
  interface RouteMeta {
    disableLocationChecks?: boolean
  }
}

const getRedirectLink = (site: SelectorSite, sitemaps: LocaleCode[]) => {
  if (site.canvasDomain && sitemaps.includes(site.locales[0].code)) {
    const host = useSubdomain() ? site.canvasDomain.replace('{subdomain}', useSubdomain()) : useHost()
    return `${host}/${site.locales[0].code}`
  }
  return `${site.domain}/${site.locales[0].path}`
}

export default defineNuxtPlugin((nuxtApp) => {
  // configStaticPagesSitemaps is an FF originally created to handle the static content sitemap
  // but, essentially, it returns the locales app and running for a given brand on Canvas.
  // To avoid FF duplications, it can be used here despite the name of the flag can be
  // slightly misleading.
  const { configStaticPagesSitemaps, enableLocationConfirmation } = useFeatureFlags()
  const host = useHost()
  const isDebug = useRouteQuery('country').value && (host.includes('netlify.app') || host.includes('localhost'))

  nuxtApp.hook('page:finish', async () => {
    // IF FF is disabled
    // OR if is in preview mode
    // OR if is a preview of a build
    // OR if is a location selector page
    if (
      !isDebug
      && (!enableLocationConfirmation
        || useRouteQuery('preview').value
        || host.includes('netlify.app')
        || useRoute().meta.disableLocationChecks)
    ) {
      return
    }

    const appCountryCode = useCountryCode()
    const confirmedCountry = useLocalStorage<Uppercase<CountryCode>>('confirmedCountry', null)

    if (confirmedCountry.value === appCountryCode) return

    const debugCountry = isDebug ? { code: (useRouteQuery('country').value as string)?.split(':')[0], name: (useRouteQuery('country').value as string)?.split(':')[1] } : undefined

    const [userCountry, brandSites] = await Promise.all([
      debugCountry || $fetch<NetlifyCountry>('/get-user-geo-country').then(({ country }) => country),
      $fetch<any>('/fapi/pages/location-selector').then((sites) => sites)
    ])

    // if country is not provided by Netlify
    // if country provided by Netlify matches with app current country
    if (!userCountry?.code || appCountryCode === userCountry.code) return

    const [lang] = navigator.language.split('-')
    const userCountryCode = userCountry.code.toLowerCase()
    const userLocale = `${lang}-${userCountryCode}` as LocaleCode
    const appLocale = useLocale()

    if (appLocale === userLocale) return

    const expectedSite = brandSites.find(({ countryCode }) => countryCode === userCountry.code)
    const actualSite = brandSites.find(({ countryCode }) => countryCode === appCountryCode)

    if (!expectedSite || !actualSite) return

    const { DialogLocationConfirmation } = useDialogsStore()

    DialogLocationConfirmation.open({
      currentCountry: actualSite.countryCode,
      currentCountryName: actualSite.name,
      currentDomain: useTld(),
      link: getRedirectLink(expectedSite, configStaticPagesSitemaps),
      expectedCountry: expectedSite.countryCode,
      expectedCountryName: expectedSite.name
    })
  })
})
